// Local-mode mocks for the RADIUS Logs page and the RADIUS-mode dashboard. Development only.
//
// /ad/GetAuthLog: a RADIUS query (protocol or mode "radius") gets the rows below, which carry
// `reason` and `radiusServerId` like authnull-service's AuthEventDTO, and honour the
// radiusServerId / challenged filters. Any other query is handed to the Auth Logs mock
// (authLogsMocks.js), so the AD log keeps its own data.
//
// /dashboardService/listActivePolicy: mode "radius" returns the RADIUS policy count; other
// modes get what the static "/dashboard" key returned before this mock existed.
//
// Server ids match the RADIUS server mocks (SERVER_LAB, SERVER_DR). The real API uses UUIDs.
//
// Order: these must be checked before AUTH_LOG_MOCKS (same "/ad/getauthlog" pattern).

import { AUTH_LOG_MOCKS } from "./authLogsMocks";
import { SERVER_DR, SERVER_LAB } from "./radiusMocks";

const NOW = Date.now();
const MIN = 60 * 1000;
const ago = (ms) => NOW - ms;

const row = (minsAgo, o) => ({
  gatewayId: "10.10.0.1",
  serviceSpn: "vpn-hq",
  tenantId: 1,
  orgId: 1,
  protocol: "radius",
  realm: "authnull.lab",
  clientIp: "203.0.113.40",
  upstreamErrorCode: 0,
  correlationId: `rad-${minsAgo}-0b1c2d3e-4f50-4a61-8b72-93a4b5c6d7e8`,
  tsUnixMs: ago(minsAgo * MIN),
  radiusServerId: SERVER_LAB,
  ...o,
});

const EVENTS = [
  row(4, { principal: "msmith", decision: "allow", reason: "Push approved · vpn-mfa", challengeId: "c-101" }),
  row(9, { principal: "jdoe", decision: "deny", reason: "Push declined · vpn-mfa", challengeId: "c-102", clientIp: "198.51.100.23" }),
  row(17, { principal: "aditya", decision: "allow", reason: "Allowed by policy · it-admins-no-mfa" }),
  row(26, {
    principal: "kpatel",
    decision: "deny",
    reason: "No answer within 60 s · vpn-mfa",
    challengeId: "c-103",
    radiusServerId: SERVER_DR,
    gatewayId: "10.20.0.1",
    serviceSpn: "vpn-dr",
  }),
  row(41, { principal: "contractor1", decision: "deny", reason: "Blocked by policy · contractors-block", radiusServerId: SERVER_DR, gatewayId: "10.20.0.1", serviceSpn: "vpn-dr" }),
  row(58, { principal: "ghost", decision: "deny", reason: "User not found in the directory" }),
  // An older bridge: no radiusServerId on the row ("Unknown server").
  row(95, { principal: "msmith", decision: "allow", reason: "Push approved · vpn-mfa", challengeId: "c-104", radiusServerId: undefined, gatewayId: "10.10.0.9", serviceSpn: "" }),
  row(130, { principal: "jdoe", decision: "allow", reason: "No matching policy", clientIp: "198.51.100.23" }),
];

function filterRadius(body = {}) {
  const principal = String(body.principal || "").toLowerCase();
  return EVENTS.filter(
    (e) =>
      (!body.decision || e.decision === body.decision) &&
      (!principal || e.principal.toLowerCase().includes(principal)) &&
      (!body.radiusServerId || e.radiusServerId === body.radiusServerId) &&
      (!body.challenged || !!e.challengeId) &&
      (!body.excludeIntermediate || e.decision !== "mfa_required") &&
      (!body.sinceUnixMs || e.tsUnixMs >= body.sinceUnixMs) &&
      (!body.untilUnixMs || e.tsUnixMs <= body.untilUnixMs),
  ).sort((a, b) => b.tsUnixMs - a.tsUnixMs);
}

function timeline(events) {
  const bucket = 15 * MIN;
  const counts = new Map();
  events.forEach((e) => {
    const t = Math.floor(e.tsUnixMs / bucket) * bucket;
    counts.set(t, (counts.get(t) || 0) + 1);
  });
  return [...counts.entries()].sort((a, b) => a[0] - b[0]).map(([tsUnixMs, count]) => ({ tsUnixMs, count }));
}

const isRadiusQuery = (body) =>
  String(body?.protocol || "").toLowerCase() === "radius" || String(body?.mode || "").toLowerCase() === "radius";

const authLogFallback = AUTH_LOG_MOCKS.find((m) => m.pattern === "/ad/getauthlog");

export const RADIUS_LOGS_MOCKS = [
  {
    pattern: "/ad/getauthlog",
    respond: (body) => {
      if (!isRadiusQuery(body)) return authLogFallback ? authLogFallback.respond(body) : { code: 200, status: "Success", total: 0, events: [], timeline: [] };
      const all = filterRadius(body || {});
      const page = Math.max(1, Number(body?.page) || 1);
      const pageSize = Math.min(100, Math.max(1, Number(body?.pageSize) || 25));
      return {
        code: 200,
        status: "Success",
        message: "ok",
        total: all.length,
        page,
        pageSize,
        events: all.slice((page - 1) * pageSize, page * pageSize),
        timeline: timeline(all),
      };
    },
  },
  {
    pattern: "/dashboardservice/listactivepolicy",
    respond: (body) =>
      String(body?.mode || "").toLowerCase() === "radius"
        ? { code: 200, status: "Success", message: "Active RADIUS policy count fetched successfully", counts: [], totalCount: 3 }
        : { code: 200, data: { totalUsers: 12, totalEndpoints: 5, totalPolicies: 3, authLogs: [] } },
  },
];
